import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { forkJoin, Observable, of, Subject, switchMap, takeUntil } from "rxjs";
import { catchError, map } from "rxjs/operators";

import {
  BillingRecordSummary,
  BillingStatus,
  Organization,
  OrganizationDataResume,
} from "../models/organizations.entity";
import { OrganizationRepository } from "../repositories/organization.repository";
import { ProfileService } from "../../users/services/profile.service";

type PortfolioFilter =
  | "all"
  | "attention"
  | "payments"
  | "renewals"
  | "no-offer";

type PortfolioAlertTone = "critical" | "warning" | "info";

type PortfolioAlert = {
  tone: PortfolioAlertTone;
  priority: number;
  label: string;
};

type PortfolioOrganizationItem = {
  organization: Organization;
  resume: OrganizationDataResume;
  billingStatus: BillingStatus | null;
  displayedUnits: number;
  journeysRemaining: number;
  planLabel: string;
  nextRenewalAt: Date | null;
  parentLabel?: string;
  seatCount: number;
  pendingPaymentRecord: BillingRecordSummary | null;
  alerts: PortfolioAlert[];
  attentionScore: number;
  hasActiveOffer: boolean;
  hasConfiguredOffer: boolean;
  hasPendingPayment: boolean;
  hasRenewalSoon: boolean;
  hasLowCredits: boolean;
  hasAttention: boolean;
};

@Component({
  selector: "app-organization-portfolio",
  templateUrl: "./organization-portfolio.component.html",
  styleUrls: ["./organization-portfolio.component.scss"],
})
export class OrganizationPortfolioComponent implements OnInit, OnDestroy {
  firstName = "";

  loading = true;

  selectedFilter: PortfolioFilter = "all";

  searchForm = new FormControl<string>("");

  portfolioItems: PortfolioOrganizationItem[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly organizationRepository: OrganizationRepository,
    private readonly profileService: ProfileService,
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle("We talk - Portefeuille");

    this.profileService
      .getMyProfile()
      .pipe(
        switchMap((profile) => {
          this.firstName = profile.firstName;
          return this.organizationRepository.findAll();
        }),
        switchMap((organizations) => {
          const uniqueOrganizations = Array.from(
            new Map(organizations.map((organization) => [organization.id, organization])).values(),
          );

          if (!uniqueOrganizations.length) {
            return of([] as PortfolioOrganizationItem[]);
          }

          return forkJoin(
            uniqueOrganizations.map((organization) => this.loadPortfolioItem(organization.id)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((items) => {
        this.portfolioItems = items
          .filter((item): item is PortfolioOrganizationItem => !!item)
          .sort((left, right) => {
            if (right.attentionScore !== left.attentionScore) {
              return right.attentionScore - left.attentionScore;
            }
            return left.organization.name.localeCompare(right.organization.name);
          });
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFilter(filter: PortfolioFilter): void {
    this.selectedFilter = filter;
  }

  goToOrganization(item: PortfolioOrganizationItem): void {
    this.router.navigate(["organizations", item.organization.id]);
  }

  goToUnits(item: PortfolioOrganizationItem): void {
    this.router.navigate(["organizations", item.organization.id, "units"]);
  }

  get filteredItems(): PortfolioOrganizationItem[] {
    const searchValue = (this.searchForm.value ?? "").trim().toLowerCase();

    return this.portfolioItems.filter((item) => {
      const matchesSearch =
        !searchValue
        || item.organization.name.toLowerCase().includes(searchValue)
        || item.parentLabel?.toLowerCase().includes(searchValue);

      if (!matchesSearch) {
        return false;
      }

      switch (this.selectedFilter) {
        case "attention":
          return item.hasAttention;
        case "payments":
          return item.hasPendingPayment;
        case "renewals":
          return item.hasRenewalSoon;
        case "no-offer":
          return !item.hasConfiguredOffer;
        default:
          return true;
      }
    });
  }

  get organizationsCount(): number {
    return this.portfolioItems.length;
  }

  get totalMembers(): number {
    return this.portfolioItems.reduce((sum, item) => sum + item.resume.members, 0);
  }

  get totalCredits(): number {
    return this.portfolioItems.reduce((sum, item) => sum + item.displayedUnits, 0);
  }

  get attentionCount(): number {
    return this.portfolioItems.filter((item) => item.hasAttention).length;
  }

  get pendingPaymentCount(): number {
    return this.portfolioItems.filter((item) => item.hasPendingPayment).length;
  }

  get topAlerts(): Array<{ organizationName: string; alert: PortfolioAlert }> {
    return this.portfolioItems
      .flatMap((item) =>
        item.alerts.map((alert) => ({
          organizationName: item.organization.name,
          alert,
        })),
      )
      .sort((left, right) => right.alert.priority - left.alert.priority)
      .slice(0, 6);
  }

  getAlertToneLabel(tone: PortfolioAlertTone): string {
    switch (tone) {
      case "critical":
        return "Priorité haute";
      case "warning":
        return "À surveiller";
      default:
        return "Info";
    }
  }

  getItemTone(item: PortfolioOrganizationItem): PortfolioAlertTone {
    if (item.alerts.some((alert) => alert.tone === "critical")) {
      return "critical";
    }
    if (item.alerts.some((alert) => alert.tone === "warning")) {
      return "warning";
    }
    return "info";
  }

  formatDate(value?: Date | string | null): string {
    if (!value) {
      return "Non disponible";
    }

    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return "Non disponible";
    }

    return dateValue.toLocaleDateString("fr-FR");
  }

  formatCurrency(amountCents?: number | null): string {
    if (amountCents === null || amountCents === undefined) {
      return "Non disponible";
    }

    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amountCents / 100);
  }

  private loadPortfolioItem(organizationId: string): Observable<PortfolioOrganizationItem | null> {
    return forkJoin({
      organization: this.organizationRepository.findById(organizationId),
      resume: this.organizationRepository.getOrganizationDataResume(organizationId).pipe(
        catchError(() =>
          of({
            quotaDemands: 0,
            unitDemands: 0,
            members: 0,
            activatedMembers: 0,
            talkers: 0,
            lifeSatisfactionScore: -1,
            workSatisfactionScore: -1,
            affectAndRelationScore: -1,
            canSeeStats: false,
          } as OrganizationDataResume),
        ),
      ),
      billingStatus: this.organizationRepository.getBillingStatus(organizationId).pipe(
        catchError(() => of(null)),
      ),
    }).pipe(
      map(({ organization, resume, billingStatus }) =>
        this.buildPortfolioItem(organization, resume, billingStatus),
      ),
      catchError(() => of(null)),
    );
  }

  private buildPortfolioItem(
    organization: Organization,
    resume: OrganizationDataResume,
    billingStatus: BillingStatus | null,
  ): PortfolioOrganizationItem {
    const displayedUnits = organization.creditSummary?.currentUnits ?? organization.units ?? 0;
    const journeysRemaining =
      (organization.creditSummary?.includedJourneysRemaining ?? 0)
      + (organization.creditSummary?.complementaryJourneysRemaining ?? 0);
    const nextRenewalAt =
      organization.activeSubscription?.nextRenewalAt
      ?? (billingStatus?.nextRenewalAt ? new Date(billingStatus.nextRenewalAt) : null);
    const hasActiveOffer = !!organization.activeSubscription;
    const hasConfiguredOffer = hasActiveOffer
      || !!organization.subscriptionPlan?.slug
      || !!organization.subscriptionPlan?.name;
    const pendingPaymentRecord =
      billingStatus?.pendingRenewalCheckout
      ?? billingStatus?.pendingSubscriptionCheckout
      ?? billingStatus?.lastComplementaryJourneysCheckout
      ?? null;
    const hasPendingPayment = pendingPaymentRecord?.status === "payment_pending";
    const journeyCreditCost = organization.creditSummary?.journeyCreditsCost
      ?? organization.creditCosts?.speechTrain
      ?? 10;
    const hasLowCredits = displayedUnits > 0 && displayedUnits <= journeyCreditCost * 2;
    const renewalDays = nextRenewalAt ? this.getDaysUntil(nextRenewalAt) : null;
    const hasRenewalSoon = renewalDays !== null && renewalDays >= 0 && renewalDays <= 30;

    const alerts: PortfolioAlert[] = [];

    if (!hasConfiguredOffer) {
      alerts.push({
        tone: "critical",
        priority: 100,
        label: "Aucune offre active",
      });
    }

    if (hasPendingPayment) {
      alerts.push({
        tone: "critical",
        priority: 95,
        label: "Paiement ou activation en attente",
      });
    }

    if (resume.unitDemands > 0) {
      alerts.push({
        tone: "warning",
        priority: 80,
        label: `${resume.unitDemands} demande${resume.unitDemands > 1 ? "s" : ""} de crédits`,
      });
    }

    if (resume.quotaDemands > 0) {
      alerts.push({
        tone: "warning",
        priority: 75,
        label: `${resume.quotaDemands} demande${resume.quotaDemands > 1 ? "s" : ""} de quota`,
      });
    }

    if (hasLowCredits) {
      alerts.push({
        tone: "warning",
        priority: 70,
        label: "Crédits bas sur le cycle en cours",
      });
    }

    if (hasRenewalSoon) {
      alerts.push({
        tone: "info",
        priority: 55,
        label: `Renouvellement proche (${this.formatDate(nextRenewalAt)})`,
      });
    }

    return {
      organization,
      resume,
      billingStatus,
      displayedUnits,
      journeysRemaining,
      planLabel: this.getPlanLabel(organization),
      nextRenewalAt,
      parentLabel: organization.parent?.name,
      seatCount: organization.activeSubscription?.seatCount ?? 0,
      pendingPaymentRecord,
      alerts,
      attentionScore: alerts.reduce((max, alert) => Math.max(max, alert.priority), 0),
      hasActiveOffer,
      hasConfiguredOffer,
      hasPendingPayment,
      hasRenewalSoon,
      hasLowCredits,
      hasAttention: alerts.length > 0,
    };
  }

  private getPlanLabel(organization: Organization): string {
    if (organization.activeSubscription?.planCode) {
      switch (organization.activeSubscription.planCode) {
        case "wetalk":
          return "We talk Basic";
        case "wetalk-plus":
          return "We talk Plus";
        case "wetalk-coach":
          return "We talk Coach";
        default:
          return organization.activeSubscription.planCode;
      }
    }

    return organization.subscriptionPlan?.name ?? "Sans offre active";
  }

  private getDaysUntil(date: Date): number {
    const today = new Date();
    const utcDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const utcToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((utcDate - utcToday) / 86400000);
  }
}
